export type RbacLevel = "none" | "view" | "write" | "delete";

export const LEVELS = {
  NONE: "none",
  VIEW: "view",
  WRITE: "write",
  DELETE: "delete",
} as const satisfies Record<string, RbacLevel>;

export const GRANT_LEVELS: RbacLevel[] = ["view", "write", "delete"];

const LEVEL_RANK: Record<RbacLevel, number> = {
  none: 0,
  view: 1,
  write: 2,
  delete: 3,
};

export function levelRank(level: RbacLevel | string | undefined): number {
  return (LEVEL_RANK as Record<string, number>)[level as string] ?? 0;
}

export function isGrantLevel(level: RbacLevel | string | undefined): boolean {
  return GRANT_LEVELS.includes(level as RbacLevel);
}

export const Level = {
  VIEW: "view",
  WRITE: "write",
  DELETE: "delete",
} as const satisfies Record<string, RbacLevel>;

export const GROUPS = {
  GENERAL: "general",
  INTELLIGENCE: "intelligence",
  CUSTOMERS: "customers",
  MESSAGING: "messaging",
  GROUPS_NOTES: "groups_notes",
  LOYALTY: "loyalty",
  BUSINESS_CARDS: "business_cards",
  ACQUISITION: "acquisition",
  EVENTS: "events",
  PAYMENTS: "payments",
  TASKS: "tasks",
  ESIGNATURE: "esignature",
  AGENDA: "agenda",
  SETTINGS: "settings",
  INTEGRATION: "integration",
} as const;

export type CapabilityGroup = (typeof GROUPS)[keyof typeof GROUPS];

export interface Capability {
  group: CapabilityGroup;
  maxLevel: RbacLevel;
  label: string;
  binary?: boolean;
}
const V: RbacLevel = "view";
const W: RbacLevel = "write";
const D: RbacLevel = "delete";

export const CAPABILITIES = {
  DASHBOARD_ACCESS: {
    group: GROUPS.GENERAL,
    maxLevel: V,
    label: "Dashboard access",
  },
  SEARCH: { group: GROUPS.INTELLIGENCE, maxLevel: V, label: "Search" },
  ADVANCED_SEARCH: {
    group: GROUPS.INTELLIGENCE,
    maxLevel: V,
    label: "Advanced search",
  },
  AI_ENGINE_SEARCH: {
    group: GROUPS.INTELLIGENCE,
    maxLevel: V,
    label: "AI search (contextual)",
  },
  AI_ENGINE_SEARCH_GLOBAL: {
    group: GROUPS.INTELLIGENCE,
    maxLevel: V,
    label: "AI search (global)",
  },
  AI_ENGINE_CONFIG: {
    group: GROUPS.INTELLIGENCE,
    maxLevel: D,
    label: "AI engine configuration",
  },
  CUSTOMERS_LIST: {
    group: GROUPS.CUSTOMERS,
    maxLevel: V,
    label: "Customers list",
  },
  USERS_DETAILS: {
    group: GROUPS.CUSTOMERS,
    maxLevel: V,
    label: "Access user profile",
  },
  USERS_DATA: { group: GROUPS.CUSTOMERS, maxLevel: D, label: "User data" },
  USERS_ACTIVITIES: {
    group: GROUPS.CUSTOMERS,
    maxLevel: V,
    label: "User activities",
  },
  USERS_FILES: { group: GROUPS.CUSTOMERS, maxLevel: D, label: "User files" },
  USERS_AGENDA: {
    group: GROUPS.CUSTOMERS,
    maxLevel: D,
    label: "Agenda (appointments and availability)",
  },
  USERS_UPDATE: {
    group: GROUPS.CUSTOMERS,
    maxLevel: W,
    label: "Edit user / verify phone",
    binary: true,
  },
  USERS_DELETE: {
    group: GROUPS.CUSTOMERS,
    maxLevel: D,
    label: "Delete user",
    binary: true,
  },
  USERS_ACCESS: {
    group: GROUPS.CUSTOMERS,
    maxLevel: W,
    label: "Ban user (reversible)",
    binary: true,
  },
  USERS_ACCESS_MANAGEMENT: {
    group: GROUPS.CUSTOMERS,
    maxLevel: W,
    label: "Send password / reset link",
    binary: true,
  },
  USERS_USER_ROLE: {
    group: GROUPS.CUSTOMERS,
    maxLevel: W,
    label: "Change user role",
  },
  USERS_USER_TYPE: {
    group: GROUPS.CUSTOMERS,
    maxLevel: W,
    label: "Change user type",
  },
  READ_A_DOCUMENT: {
    group: GROUPS.CUSTOMERS,
    maxLevel: W,
    label: "Read a document (OCR import)",
    binary: true,
  },
  IMPORT_USERS: {
    group: GROUPS.CUSTOMERS,
    maxLevel: W,
    label: "Import users / add manually",
    binary: true,
  },
  CREATE_PAYMENT_LINK: {
    group: GROUPS.CUSTOMERS,
    maxLevel: W,
    label: "Manage user payment link",
  },
  SEND_FOR_ESIGNATURE: {
    group: GROUPS.CUSTOMERS,
    maxLevel: W,
    label: "Manage user e-signature",
  },
  SEND_EMAIL: {
    group: GROUPS.MESSAGING,
    maxLevel: W,
    label: "Send email",
    binary: true,
  },
  SEND_SMS: {
    group: GROUPS.MESSAGING,
    maxLevel: W,
    label: "Send SMS",
    binary: true,
  },
  SEND_WHATSAPP: {
    group: GROUPS.MESSAGING,
    maxLevel: W,
    label: "Send WhatsApp",
    binary: true,
  },
  BULK_MESSAGING: {
    group: GROUPS.MESSAGING,
    maxLevel: W,
    label: "Bulk messaging",
    binary: true,
  },
  GROUP_MANAGEMENT: {
    group: GROUPS.GROUPS_NOTES,
    maxLevel: D,
    label: "Groups & segments",
  },
  USER_NOTES: {
    group: GROUPS.GROUPS_NOTES,
    maxLevel: D,
    label: "Customer notes",
  },
  LOYALTY: { group: GROUPS.LOYALTY, maxLevel: W, label: "Loyalty programs" },
  BUSINESS_CARDS: {
    group: GROUPS.BUSINESS_CARDS,
    maxLevel: D,
    label: "Business cards",
  },
  BUSINESS_CARDS_STATS: {
    group: GROUPS.BUSINESS_CARDS,
    maxLevel: V,
    label: "Business card statistics",
  },
  BUSINESS_CARDS_OPTIONS: {
    group: GROUPS.BUSINESS_CARDS,
    maxLevel: W,
    label: "Business card options",
    binary: true,
  },
  BUSINESS_CARDS_ATTRIBUTION: {
    group: GROUPS.BUSINESS_CARDS,
    maxLevel: W,
    label: "Business card attribution",
    binary: true,
  },
  SIGNUP_FORMS: {
    group: GROUPS.ACQUISITION,
    maxLevel: D,
    label: "Signup forms",
  },
  LEADS: { group: GROUPS.ACQUISITION, maxLevel: D, label: "Leads" },
  EVENTS: { group: GROUPS.EVENTS, maxLevel: D, label: "Events / ticketing" },
  PAYMENT_CONFIG: {
    group: GROUPS.PAYMENTS,
    maxLevel: D,
    label: "Payment configuration",
  },
  TASKS_BOARDS: { group: GROUPS.TASKS, maxLevel: D, label: "Task boards" },
  TASKS_BOARD_CONFIG: {
    group: GROUPS.TASKS,
    maxLevel: W,
    label: "Board badges & columns",
    binary: true,
  },
  TASKS_BOARD_TASKS: { group: GROUPS.TASKS, maxLevel: D, label: "Tasks" },
  ESIGNATURE_CONFIG: {
    group: GROUPS.ESIGNATURE,
    maxLevel: D,
    label: "E-signature configuration",
  },
  AGENDA_SETTINGS: {
    group: GROUPS.AGENDA,
    maxLevel: W,
    label: "Configure the agenda (types, notifications)",
    binary: true,
  },
  ENTITY_SETTINGS: {
    group: GROUPS.SETTINGS,
    maxLevel: W,
    label: "Business settings",
  },
  ROLE_MANAGEMENT: {
    group: GROUPS.SETTINGS,
    maxLevel: W,
    label: "Roles & permissions",
  },
  EXPORT_DATA: { group: GROUPS.SETTINGS, maxLevel: V, label: "Data export" },
  APIS: { group: GROUPS.INTEGRATION, maxLevel: W, label: "API & login pages" },
} satisfies Record<string, Capability>;

export type CapabilityKey = keyof typeof CAPABILITIES;

export const Cap = Object.fromEntries(
  (Object.keys(CAPABILITIES) as CapabilityKey[]).map((k) => [k, k])
) as { [K in CapabilityKey]: K };

export type RoleMatrix = Record<string, RbacLevel>;

export function capabilityMaxLevel(capabilityKey: string): RbacLevel | null {
  return (
    (CAPABILITIES as Record<string, Capability>)[capabilityKey]?.maxLevel ??
    null
  );
}

export function capabilitySupportsLevel(
  capabilityKey: string,
  level: RbacLevel
): boolean {
  const max = capabilityMaxLevel(capabilityKey);
  if (!max || !isGrantLevel(level)) return false;
  return levelRank(level) <= levelRank(max);
}

export function capabilityGrantLevels(capabilityKey: string): RbacLevel[] {
  const cap = (CAPABILITIES as Record<string, Capability>)[capabilityKey];
  if (!cap) return [];
  if (cap.binary) return [cap.maxLevel];
  return GRANT_LEVELS.filter(
    (lvl) => levelRank(lvl) <= levelRank(cap.maxLevel)
  );
}

export function setLevel(
  matrix: RoleMatrix,
  capabilityKey: string,
  level: RbacLevel
): RoleMatrix {
  const next: RoleMatrix = { ...matrix };
  const max = capabilityMaxLevel(capabilityKey);
  const capped: RbacLevel =
    max && levelRank(level) > levelRank(max) ? max : level;
  if (capped === "none") {
    delete next[capabilityKey];
  } else {
    next[capabilityKey] = capped;
  }
  return next;
}

export function getMatrixCatalog(): Array<{
  group: CapabilityGroup;
  capabilities: Array<{ key: string; label: string; maxLevel: RbacLevel }>;
}> {
  const byGroup: Record<
    string,
    Array<{ key: string; label: string; maxLevel: RbacLevel }>
  > = {};
  for (const key of Object.keys(CAPABILITIES) as CapabilityKey[]) {
    const cap = CAPABILITIES[key];
    (byGroup[cap.group] ||= []).push({
      key,
      label: cap.label,
      maxLevel: cap.maxLevel,
    });
  }
  return Object.values(GROUPS)
    .filter((group) => byGroup[group])
    .map((group) => ({ group, capabilities: byGroup[group] }));
}
